import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { notificationAPI, authAPI } from '../services/api';
import websocketService from '../services/websocketService';
import { tokenManager } from '../services/api';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// The dropdown is a quick-triage surface, not a full archive browser — a
// 50-item window per view keeps it snappy and is plenty for everyday use.
const PAGE_LIMIT = 50;

export default function NotificationProvider({ children }) {
  const { user } = useAuth();
  // The cached user object carries `id` (normalized in tokenManager.getUser),
  // while API-fetched profiles carry Mongo's `_id`. Guarding on `_id` alone
  // silently disabled every notification action (and the WebSocket) for
  // sessions holding the cached shape — accept either.
  const userId = user?._id || user?.id;

  // Inbox = non-archived notifications. Archived is loaded lazily the first
  // time the user opens the Archived segment.
  const [notifications, setNotifications] = useState([]);
  const [archived, setArchived] = useState([]);
  const [archivedLoaded, setArchivedLoaded] = useState(false);

  // Server-authoritative tab counts; resynced on every fetch and nudged
  // optimistically in between so the UI stays responsive.
  const [counts, setCounts] = useState({ inbox: 0, unread: 0, archived: 0 });

  const [showNotifications, setShowNotifications] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!userId || !tokenManager.getToken()) return;
    setLoading(true);
    try {
      const res = await notificationAPI.getNotifications({ view: 'inbox', limit: PAGE_LIMIT });
      if (res?.success) {
        setNotifications(res.data?.notifications || []);
        if (res.data?.counts) setCounts(res.data.counts);
      }
    } catch (err) {
      if (userId) console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchArchived = useCallback(async () => {
    if (!userId || !tokenManager.getToken()) return;
    try {
      const res = await notificationAPI.getNotifications({ view: 'archived', limit: PAGE_LIMIT });
      if (res?.success) {
        setArchived(res.data?.notifications || []);
        if (res.data?.counts) setCounts(res.data.counts);
        setArchivedLoaded(true);
      }
    } catch (err) {
      if (userId) console.error('Error fetching archived notifications:', err);
    }
  }, [userId]);

  // ── WebSocket: realtime delivery ───────────────────────────────────────
  useEffect(() => {
    // The provider outlives login sessions, so every account change (including
    // logout → null) must wipe the previous user's rows and counts. Without
    // this, switching accounts in one browser leaks the prior user's
    // notifications into the new session and leaves an unclearable unread dot.
    setNotifications([]);
    setArchived([]);
    setArchivedLoaded(false);
    setCounts({ inbox: 0, unread: 0, archived: 0 });

    const token = tokenManager.getToken();
    if (!token || !userId) return undefined;

    websocketService.connect(() => authAPI.getWsTicket().then((r) => r?.data?.ticket));

    const handleNotification = (notification) => {
      setNotifications((prev) => [notification, ...prev]);
      setCounts((prev) => ({
        ...prev,
        inbox: prev.inbox + 1,
        unread: prev.unread + 1,
      }));
      // Mirror to the OS notification centre when the user has allowed it.
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(notification.title, {
          body: notification.message,
          icon: '/favicon.ico',
          tag: notification._id,
        });
      }
    };
    const handleConnected = () => setIsConnected(true);
    const handleDisconnected = () => setIsConnected(false);
    const handleError = () => setIsConnected(false);

    websocketService.on('notification', handleNotification);
    websocketService.on('connected', handleConnected);
    websocketService.on('disconnected', handleDisconnected);
    websocketService.on('error', handleError);

    // Initial inbox load.
    fetchNotifications();

    return () => {
      websocketService.off('notification', handleNotification);
      websocketService.off('connected', handleConnected);
      websocketService.off('disconnected', handleDisconnected);
      websocketService.off('error', handleError);
      websocketService.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Locally-injected notification (used by the dev test helper below).
  const addNotification = useCallback((notification) => {
    const newNotification = {
      _id: Date.now().toString(),
      ...notification,
      createdAt: new Date().toISOString(),
      read: false,
      archived: false,
    };
    setNotifications((prev) => [newNotification, ...prev]);
    setCounts((prev) => ({ ...prev, inbox: prev.inbox + 1, unread: prev.unread + 1 }));
  }, []);

  const markAsRead = useCallback(async (notificationId) => {
    if (!userId || !tokenManager.getToken()) return;
    const target = notifications.find((n) => n._id === notificationId);
    if (target && target.read) return; // already read — nothing to do
    try {
      await notificationAPI.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n._id === notificationId ? { ...n, read: true } : n))
      );
      setCounts((prev) => ({ ...prev, unread: Math.max(0, prev.unread - 1) }));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  }, [userId, notifications]);

  const markAllAsRead = useCallback(async () => {
    if (!userId || !tokenManager.getToken()) return;
    try {
      await notificationAPI.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setCounts((prev) => ({ ...prev, unread: 0 }));
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }, [userId]);

  const archiveNotification = useCallback(async (notificationId) => {
    if (!userId || !tokenManager.getToken()) return;
    const item = notifications.find((n) => n._id === notificationId);
    try {
      await notificationAPI.archive(notificationId);
      setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
      if (item && archivedLoaded) {
        const archivedItem = {
          ...item,
          read: true,
          archived: true,
          archivedAt: new Date().toISOString(),
        };
        setArchived((prev) => [archivedItem, ...prev]);
      }
      setCounts((prev) => ({
        inbox: Math.max(0, prev.inbox - 1),
        unread: item && !item.read ? Math.max(0, prev.unread - 1) : prev.unread,
        archived: prev.archived + 1,
      }));
    } catch (err) {
      console.error('Error archiving notification:', err);
    }
  }, [userId, notifications, archivedLoaded]);

  const unarchiveNotification = useCallback(async (notificationId) => {
    if (!userId || !tokenManager.getToken()) return;
    const item = archived.find((n) => n._id === notificationId);
    try {
      await notificationAPI.unarchive(notificationId);
      setArchived((prev) => prev.filter((n) => n._id !== notificationId));
      if (item) {
        const restored = { ...item, archived: false, archivedAt: null };
        setNotifications((prev) =>
          [restored, ...prev].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          )
        );
      }
      setCounts((prev) => ({
        ...prev,
        inbox: prev.inbox + 1,
        archived: Math.max(0, prev.archived - 1),
      }));
    } catch (err) {
      console.error('Error restoring notification:', err);
    }
  }, [userId, archived]);

  const archiveAllRead = useCallback(async () => {
    if (!userId || !tokenManager.getToken()) return;
    try {
      await notificationAPI.archiveRead();
      await fetchNotifications();
      if (archivedLoaded) await fetchArchived();
    } catch (err) {
      console.error('Error archiving read notifications:', err);
    }
  }, [userId, fetchNotifications, fetchArchived, archivedLoaded]);

  const deleteNotification = useCallback(async (notificationId) => {
    if (!userId || !tokenManager.getToken()) return;
    const inInbox = notifications.find((n) => n._id === notificationId);
    const inArchived = archived.find((n) => n._id === notificationId);
    try {
      await notificationAPI.deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
      setArchived((prev) => prev.filter((n) => n._id !== notificationId));
      setCounts((prev) => ({
        inbox: inInbox ? Math.max(0, prev.inbox - 1) : prev.inbox,
        unread: inInbox && !inInbox.read ? Math.max(0, prev.unread - 1) : prev.unread,
        archived: inArchived ? Math.max(0, prev.archived - 1) : prev.archived,
      }));
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  }, [userId, notifications, archived]);

  // Request browser notification permission once.
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Dev-only debugging helpers exposed on window.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return undefined;
    window.testWebSocket = () => {
      const token = tokenManager.getToken();
      if (token) {
        const ws = new WebSocket(`${websocketService.url}?token=${token}`);
        ws.onopen = () => ws.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
      }
    };
    window.testNotification = () => {
      addNotification({
        title: 'Test Notification',
        message: 'This is a test notification to verify the system is working',
        type: 'system',
      });
    };
    window.refreshNotifications = () => fetchNotifications();
    window.getNotificationState = () => ({
      inbox: notifications.length,
      archived: archived.length,
      counts,
      isConnected,
      userId: userId,
      wsState: websocketService.getConnectionState?.(),
      wsUrl: websocketService.url,
    });
    window.forceReconnect = () => {
      const token = tokenManager.getToken();
      if (token && userId) {
        websocketService.disconnect();
        setTimeout(() => websocketService.connect(token), 1000);
      }
    };
    return undefined;
  }, [addNotification, fetchNotifications, notifications.length, archived.length, counts, isConnected, userId]);

  // Memoized so consumers don't re-render on every provider render.
  const value = useMemo(() => ({
    notifications,
    archived,
    archivedLoaded,
    counts,
    unreadCount: counts.unread,
    loading,
    showNotifications,
    setShowNotifications,
    isConnected,
    addNotification,
    fetchNotifications,
    fetchArchived,
    markAsRead,
    markAllAsRead,
    archiveNotification,
    unarchiveNotification,
    archiveAllRead,
    deleteNotification,
    refreshNotifications: fetchNotifications,
  }), [
    notifications, archived, archivedLoaded, counts, loading, showNotifications,
    setShowNotifications, isConnected, addNotification, fetchNotifications, fetchArchived,
    markAsRead, markAllAsRead, archiveNotification, unarchiveNotification, archiveAllRead,
    deleteNotification,
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export { NotificationProvider };
